// =============================================================================
// FILE: packages/renderer/src/components/dashboard/IntelligenceFeed.tsx
// PURPOSE: Founder-controlled industry intelligence feed on the main dashboard.
//          Shows editorial posts, tech updates, and partnership opportunities.
// =============================================================================

import type { JSX } from 'react';
import { ExternalLink, BookOpen, Zap, Handshake, Pin } from 'lucide-react';
import { FEED_POSTS, type FeedPost, type FeedPostType } from '../../config/feed.config.js';

// ── Icon + label per post type ────────────────────────────────────────────────
const TYPE_META: Record<FeedPostType, { label: string; icon: typeof BookOpen; color: string }> = {
  editorial:   { label: 'Founder\'s Editorial', icon: BookOpen,  color: '#FCD34D' },
  tech_update: { label: 'Tech Update',          icon: Zap,       color: '#25B5A8' },
  partnership: { label: 'Partnership',          icon: Handshake, color: '#34D399' },
};

function PostCard({ post }: { post: FeedPost }): JSX.Element {
  const meta    = TYPE_META[post.type];
  const Icon    = meta.icon;
  const accent  = post.accentColor ?? meta.color;
  const dateStr = new Date(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  return (
    <div
      data-testid={`feed-post-${post.id}`}
      style={{
        position:     'relative',
        borderRadius: 10,
        overflow:     'hidden',
        background:   'linear-gradient(160deg, rgba(15,30,62,0.70) 0%, rgba(10,22,46,0.65) 100%)',
        border:       `1px solid ${accent}28`,
        backdropFilter: 'blur(12px)',
        boxShadow:    post.featured
          ? `0 0 0 1px ${accent}25, 0 4px 24px rgba(0,0,0,0.35), 0 0 40px ${accent}0A`
          : `0 2px 12px rgba(0,0,0,0.25)`,
        transition:   'transform 0.18s ease, box-shadow 0.18s ease',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-2px)';
        (e.currentTarget as HTMLDivElement).style.boxShadow = `0 0 0 1px ${accent}40, 0 8px 32px rgba(0,0,0,0.4), 0 0 48px ${accent}12`;
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.transform = '';
        (e.currentTarget as HTMLDivElement).style.boxShadow = post.featured
          ? `0 0 0 1px ${accent}25, 0 4px 24px rgba(0,0,0,0.35), 0 0 40px ${accent}0A`
          : `0 2px 12px rgba(0,0,0,0.25)`;
      }}
    >
      {/* Left accent bar */}
      <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: `linear-gradient(180deg, ${accent}CC 0%, ${accent}44 100%)` }} />

      <div style={{ padding: '14px 16px 14px 19px' }}>
        {/* Header row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ padding: '3px 5px', borderRadius: 5, background: `${accent}18`, border: `1px solid ${accent}30`, display: 'flex', alignItems: 'center', gap: 4 }}>
              <Icon style={{ width: 10, height: 10, color: accent }} strokeWidth={2} />
              <span style={{ fontSize: 8, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: accent }}>
                {meta.label}
              </span>
            </div>
            {post.featured && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '2px 6px', borderRadius: 99, background: 'rgba(252,211,77,0.10)', border: '1px solid rgba(252,211,77,0.30)' }}>
                <Pin style={{ width: 8, height: 8, color: '#FCD34D' }} strokeWidth={2.5} />
                <span style={{ fontSize: 7, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(252,211,77,0.85)' }}>Pinned</span>
              </div>
            )}
          </div>
          <span style={{ fontSize: 9, color: 'rgba(148,172,210,0.40)', fontFamily: 'monospace' }}>{dateStr}</span>
        </div>

        {/* Title */}
        <div style={{ fontSize: 13, fontWeight: 600, color: 'rgba(226,236,250,0.92)', letterSpacing: '0.01em', marginBottom: 6, lineHeight: 1.35 }}>
          {post.title}
        </div>

        {/* Summary */}
        <p style={{ fontSize: 11, color: 'rgba(148,172,210,0.65)', lineHeight: 1.6, margin: 0, marginBottom: 10 }}>
          {post.summary}
        </p>

        {/* Footer row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {/* Tags */}
            {post.tags?.slice(0, 3).map((tag) => (
              <span
                key={tag}
                style={{ fontSize: 8, padding: '1px 6px', borderRadius: 99, background: `${accent}0F`, color: `${accent}AA`, border: `1px solid ${accent}20`, letterSpacing: '0.10em' }}
              >
                {tag}
              </span>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 9, color: 'rgba(148,172,210,0.40)' }}>{post.author}</span>
            {post.link && (
              <a
                href={post.link}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 9, color: accent, opacity: 0.75, textDecoration: 'none' }}
              >
                <ExternalLink style={{ width: 10, height: 10 }} />
                Learn more
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function IntelligenceFeed(): JSX.Element {
  // Show featured post first, then rest sorted by date desc
  const sorted = [...FEED_POSTS].sort((a, b) => {
    if (a.featured && !b.featured) return -1;
    if (!a.featured && b.featured) return 1;
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  });

  return (
    <section data-testid="intelligence-feed" className="mt-8">
      {/* Section header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ padding: '5px 7px', borderRadius: 8, background: 'rgba(252,211,77,0.10)', border: '1px solid rgba(252,211,77,0.22)' }}>
            <Zap style={{ width: 14, height: 14, color: '#FCD34D' }} strokeWidth={1.6} />
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'rgba(226,236,250,0.88)', letterSpacing: '0.02em' }}>
              Industry Intelligence
            </div>
            <div style={{ fontSize: 9, color: 'rgba(148,172,210,0.40)', letterSpacing: '0.16em', textTransform: 'uppercase', marginTop: 1 }}>
              Founder's Feed · Tech Updates · Partnership Opportunities
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 99, background: 'rgba(37,181,168,0.10)', border: '1px solid rgba(37,181,168,0.22)' }}>
          <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#25B5A8', boxShadow: '0 0 6px #25B5A8' }} />
          <span style={{ fontSize: 8, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(37,181,168,0.80)' }}>Live</span>
        </div>
      </div>

      {/* Feed grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 12 }}>
        {sorted.map((post) => (
          <PostCard key={post.id} post={post} />
        ))}
      </div>
    </section>
  );
}
